import { getRowRootType, usesRowIdFilter } from '@iheartjane/menu';

import type { ProductFacets } from '@jane/search/data-access';
import type { Store } from '@jane/shared/types';

import type { MenuCategory } from './menuCategory';

interface VisibleTabsProps {
  allSpecialsHidden?: boolean;
  facets?: ProductFacets;
  hasBrandSpecials: boolean;
  hasSpecials: boolean;
  store?: Store;
}

export const visibleTabs = ({
  facets,
  hasBrandSpecials,
  hasSpecials,
  store,
  allSpecialsHidden,
}: VisibleTabsProps): MenuCategory[] => {
  const rootTypes = facets?.root_types ?? {};
  const customMenuRowIds = facets?.custom_menu_row_ids ?? {};

  const {
    menu_tabs: menuTabs = [],
    /** @todo dedupe with customRowSettings */
    custom_rows: customRows,
    custom_row_settings: customRowSettings,
    id: storeId,
    specials_row_configs: specialsConfig,
  } = store ?? {};

  const rowProductCount = (
    row: NonNullable<typeof customRowSettings>[number]
  ) =>
    (usesRowIdFilter(row) ? customMenuRowIds[String(row.id)] : undefined) ??
    rootTypes[getRowRootType(row, Number(storeId))];

  return menuTabs.reduce<MenuCategory[]>((categories, tab) => {
    if (!facets) {
      return [];
    }

    const showSpecials =
      hasSpecials && (specialsConfig?.enabled || customRows?.includes('sale'));
    const authEnabled =
      store?.kiosk_allow_auth && store?.kiosk_anonymous_checkout !== 'required';

    if (tab === 'featured') {
      const hasMagicRow = customRows?.includes('magic_row');
      const hasThematicRows = customRows?.includes('custom');
      const hasBrandSpecialsRow = customRows?.includes('brand_specials');

      const hasRows =
        hasBrandSpecialsRow || hasThematicRows || hasMagicRow || showSpecials;

      const hasProducts =
        (customRowSettings || []).filter(
          (row) => (rowProductCount(row) ?? -1) > 0
        ).length > 0;

      return hasRows || hasProducts ? [...categories, tab] : categories;
    }

    if (tab === 'specials' && showSpecials && !allSpecialsHidden) {
      return showSpecials ? [...categories, tab] : categories;
    }

    if (tab === 'brand_specials' && hasBrandSpecials && authEnabled) {
      return [...categories, 'jane-gold'];
    }

    if (tab === 'all-products') {
      return [...categories, 'all'];
    }

    if (rootTypes[tab]) {
      return [...categories, tab as MenuCategory];
    }

    return categories;
  }, []);
};
